/* -------------------------------------------------------------------------- */
/*  KCCO (Kingery) – Sage JCE export                                         */
/* -------------------------------------------------------------------------- *
 *  - cost-codes: read from "Cost Code" sort field on each line/markup       *
 *  - cost-types: read from per-LMSEO "Cost Category" sort fields           *
 *  - date format: MMDDYYYY for data records, MM/DD/YYYY for header comments *
 *  - header record ("*") provides project import                             *
 *  - validation: unmatched markups, invalid line items, grand total         *
 *  - returns [stringLines, "jce"]                                            */
/* -------------------------------------------------------------------------- */

import { ValidationError } from "../../errors";
import { browserStore } from "../../utils/storageKeys";
import { identifyCategory, CategoryMap } from "./categories";
import { BudgetImportConfig, BudgetRow } from "./framework/types";

/**
 * Map from default cost type → sort field name for overrides.
 * If a user has assigned a Cost Category sort field to a line item,
 * that value overrides the default L/M/S/E/O cost type letter.
 */
const COST_CAT_FIELDS: Record<string, string> = {
  L: "Labor Cost Category_code",
  M: "Material Cost Category_code",
  S: "Subcontractor Cost Category_code",
  E: "Equipment Cost Category_code",
  O: "Other Cost Category_code",
};

/* ───────────────────────── 1. Helpers ──────────────────────────────────── */

/**
 * Formats date as MM/DD/YYYY for header comment lines.
 */
function formatDateDisplay(d: Date = new Date()): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Returns the effective cost type letter for a given default,
 * checking the corresponding Cost Category sort field for an override.
 */
function resolveCostType(row: BudgetRow, defaultType: string): string {
  const field = COST_CAT_FIELDS[defaultType];
  const override = field ? (row[field] ?? "").trim() : "";
  return override || defaultType;
}

/**
 * Builds a CategoryMap for a row, resolving cost type overrides
 * from the corresponding Cost Category sort fields.
 */
function buildCategoryMap(row: BudgetRow): CategoryMap {
  return {
    labor: resolveCostType(row, "L"),
    material: resolveCostType(row, "M"),
    equip: resolveCostType(row, "E"),
    sub: resolveCostType(row, "S"),
    other: resolveCostType(row, "O"),
    total: resolveCostType(row, "O"),
  };
}

/* ───────────────────────── 2. Config ────────────────────────────────────── */

export const kccoConfig: BudgetImportConfig = {
  format: "sage",
  requires: { floorAreas: true, project: true },
  sage: {
    ext: "jce",
    render: (ctx) => {
      const estimateRows = ctx.rows;
      const report = ctx.estimateReport;
      const project = ctx.project!;

      /* Validation tracking */
      interface UnmatchedMarkup {
        name: string;
        total: number;
      }

      interface InvalidLineItem {
        name: string;
        code: string;
      }

      const unmatchedMarkups: UnmatchedMarkup[] = [];
      const invalidLineItems: InvalidLineItem[] = [];
      let sourceTotal = 0;
      let exportTotal = 0;

      /* Project header ("*") */
      const addr1 = "";
      const city = "";
      const state = "";
      const zip = "";
      const totalArea = ctx.totalArea;

      const now = new Date();
      const dateStr = formatDateDisplay(now);
      const timeStr = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      // Get user name from profile
      let userName = "Unknown User";
      try {
        const userProfileJson = browserStore.get("userProfile");
        if (userProfileJson) {
          const userProfile = JSON.parse(userProfileJson);
          userName = userProfile?.full_name || "Unknown User";
        }
      } catch (error) {
        // Fallback to unknown if profile can't be read
        userName = "Unknown User";
      }

      /* Build output lines */
      const lines: string[] = [
        `#,"Previously Imported","by ${userName}","on ${dateStr}","at ${timeStr}"`,
        "; Estimate to Job Cost Transfer File",
        `; Created:  ${dateStr}`,
        ";",
      ];

      // Header record
      const header = [
        '"*"',
        `"${report["meta_Project Number"] || "99999"}"`,
        `"${project.name}"`,
        `"${addr1}"`,
        `"${city}"`,
        `"${state}"`,
        `"${zip}"`,
        '""',
        '""',
        '""',
        '""',
        '""',
        totalArea,
        '""',
      ].join(",");
      lines.push(header);

      /* Process estimate rows */
      const dateNum = ctx.dateNum;
      const costLines: Array<{ costCode: string; line: string }> = [];

      for (const row of estimateRows) {
        if (row.type === "line_item") {
          sourceTotal += Number(row.total) || 0;
          // Validate cost code exists (KCCO uses "Cost Code_code" field)
          const costCode = row["Cost Code_code"];
          if (!costCode || costCode.trim() === "") {
            invalidLineItems.push({
              name: row.name || "Unknown line item",
              code: costCode || "(missing)",
            });
            continue;
          }

          // Split by cost categories
          const categories = identifyCategory(row, buildCategoryMap(row));
          for (const [category, unitCost] of categories) {
            const amount = (row.quantity ?? 0) * unitCost;

            // Skip lines with 0 cost
            if (amount === 0) {
              continue;
            }

            exportTotal += amount;

            costLines.push({
              costCode,
              line: ['"C"', `"${costCode}"`, '""', `"${category}"`, dateNum, '""', '""', amount.toFixed(2)].join(","),
            });
          }
        }

        if (row.type === "markup") {
          sourceTotal += Number(row.total) || 0;

          // Skip markups with 0 or missing cost
          if (!row.total) {
            continue;
          }

          const costCode = row["Cost Code_code"];
          if (!costCode || costCode.trim() === "") {
            unmatchedMarkups.push({
              name: row.name,
              total: Number(row.total) || 0,
            });
            continue;
          }

          exportTotal += Number(row.total) || 0;

          const markupCostType = (row["Markup Cost Category_code"] ?? "").trim() || "O";
          costLines.push({
            costCode,
            line: [
              '"C"',
              `"${costCode}"`,
              '""',
              `"${markupCostType}"`,
              dateNum,
              '""',
              '""',
              Number(row.total).toFixed(2),
            ].join(","),
          });
        }
      }

      // Sort C lines by cost code
      costLines.sort((a, b) => a.costCode.localeCompare(b.costCode));
      lines.push(...costLines.map((cl) => cl.line));

      /* Three-tier validation */

      // Validation 1: Unmatched markups
      if (unmatchedMarkups.length > 0) {
        const displayCount = Math.min(5, unmatchedMarkups.length);
        const markupNames = unmatchedMarkups
          .slice(0, displayCount)
          .map((m) => m.name)
          .join(", ");
        const remaining = unmatchedMarkups.length - displayCount;
        const suffix = remaining > 0 ? `, and ${remaining} more` : "";

        throw new ValidationError(
          `${unmatchedMarkups.length} markup(s) missing a Cost Code sort field — assign one in the app: ${markupNames}${suffix}`
        );
      }

      // Validation 2: Invalid line items
      if (invalidLineItems.length > 0) {
        const displayCount = Math.min(5, invalidLineItems.length);
        const itemNames = invalidLineItems
          .slice(0, displayCount)
          .map((i) => i.name)
          .join(", ");
        const remaining = invalidLineItems.length - displayCount;
        const suffix = remaining > 0 ? `, and ${remaining} more` : "";

        throw new ValidationError(
          `${invalidLineItems.length} line item(s) have invalid/missing cost codes: ${itemNames}${suffix}`
        );
      }

      // Validation 3: Grand total agreement
      const tolerance = 0.01;
      if (Math.abs(sourceTotal - exportTotal) > tolerance) {
        const diff = Math.abs(sourceTotal - exportTotal).toFixed(2);
        throw new ValidationError(`Export total mismatch: difference of $${diff}`);
      }

      return lines;
    },
  },
};
